import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";

const empty = {
  id: "",
  name: "",
  description: "",
  price: 0,
  stock: 0,
  category_id: "",
  image_url: "",
  images: [] as string[],
  colors: [] as string[],
  color_images: {} as Record<string, string[]>,
  sizes: [] as string[],
  size_stock: {} as Record<string, number>,
  featured: false,
  on_sale: false,
  sale_price: 0,
  size_chart_enabled: false,
  size_chart_image_url: "" as string,
  size_chart: { headers: ["Size", "Chest", "Length"], rows: [["M", "38", "40"], ["L", "40", "42"], ["XL", "42", "44"]] } as { headers: string[]; rows: string[][] },
};

const PRESET_SIZES = [
  { label: "Apparel", values: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"] },
  { label: "Numeric", values: ["38", "40", "42", "44", "46"] },
  { label: "Other", values: ["Free Size"] },
];

type Category = { id: string; name: string };


const AdminProducts = () => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [colorsInput, setColorsInput] = useState("");
  const [sizesInput, setSizesInput] = useState("");
  const formRef = useRef<any>(empty);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Normalize a single color token: trim, expand #abc → #aabbcc, uppercase hex
  const normalizeColor = (raw: string): string | null => {
    const v = raw.trim();
    if (!v) return null;
    if (v.startsWith("#")) {
      const hex = v.slice(1);
      if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        return "#" + hex.split("").map((c) => c + c).join("").toUpperCase();
      }
      if (/^[0-9a-fA-F]{6}$/.test(hex)) {
        return "#" + hex.toUpperCase();
      }
      return null; // invalid hex
    }
    return v;
  };

  const parseColorsInput = (text: string): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const part of text.split(",")) {
      const norm = normalizeColor(part);
      if (!norm) continue;
      const key = norm.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(norm);
    }
    return out;
  };

  const cleanColorImages = (map: Record<string, string[]> = {}, colors: string[] = []) => {
    const cleaned: Record<string, string[]> = {};
    for (const c of colors) {
      const arr = (map[c] ?? []).filter((u: string) => !!u);
      if (arr.length) cleaned[c] = arr;
    }
    return cleaned;
  };

  const commitColorsInput = () => {
    const parsed = parseColorsInput(colorsInput);
    setForm((f: any) => ({ ...f, colors: parsed }));
    setColorsInput(parsed.join(", "));
  };

  const removeColor = (color: string) => {
    setForm((f: any) => {
      const next = (f.colors ?? []).filter((c: string) => c !== color);
      setColorsInput(next.join(", "));
      return { ...f, colors: next };
    });
  };

  const categoryName = (id?: string | null) =>
    id ? categories.find((c) => c.id === id)?.name ?? "—" : "—";

  const load = async () => {
    setLoading(true);
    const [prodRes, catRes] = await Promise.all([
      supabase.from("products").select("*").order("created_at", { ascending: false }),
      supabase.from("categories").select("id, name").order("name", { ascending: true }),
    ]);
    setProducts(prodRes.data ?? []);
    setCategories((catRes.data ?? []) as Category[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setColorsInput(""); setSizesInput(""); setOpen(true); };
  const openEdit = (p: any) => {
    const colors = p.colors ?? [];
    const sizes = p.sizes ?? [];
    const chart = (p.size_chart && typeof p.size_chart === "object")
      ? {
          headers: Array.isArray(p.size_chart.headers) ? p.size_chart.headers : [],
          rows: Array.isArray(p.size_chart.rows) ? p.size_chart.rows : [],
        }
      : { headers: [], rows: [] };
    setForm({
      ...p,
      category_id: p.category_id ?? "",
      sale_price: p.sale_price ?? 0,
      images: p.images ?? [],
      colors,
      color_images: (p.color_images && typeof p.color_images === "object") ? p.color_images : {},
      sizes,
      size_stock: (p.size_stock && typeof p.size_stock === "object") ? p.size_stock : {},
      size_chart_enabled: !!p.size_chart_enabled,
      size_chart_image_url: p.size_chart_image_url ?? "",
      size_chart: chart,
    });
    setColorsInput(colors.join(", "));
    setSizesInput(sizes.join(", "));
    setOpen(true);
  };

  const parseSizes = (text: string): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const part of text.split(",")) {
      const v = part.trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out;
  };

  const toggleSize = (s: string) => {
    setForm((f: any) => {
      const cur: string[] = f.sizes ?? [];
      const exists = cur.some((x) => x.toLowerCase() === s.toLowerCase());
      const next = exists ? cur.filter((x) => x.toLowerCase() !== s.toLowerCase()) : [...cur, s];
      setSizesInput(next.join(", "));
      const nextStock = { ...(f.size_stock ?? {}) };
      if (exists) delete nextStock[s];
      return { ...f, sizes: next, size_stock: nextStock };
    });
  };

  const removeSize = (s: string) => {
    setForm((f: any) => {
      const next = (f.sizes ?? []).filter((x: string) => x !== s);
      setSizesInput(next.join(", "));
      const nextStock = { ...(f.size_stock ?? {}) };
      delete nextStock[s];
      return { ...f, sizes: next, size_stock: nextStock };
    });
  };

  const setSizeStock = (s: string, val: string) => {
    setForm((f: any) => {
      const nextStock = { ...(f.size_stock ?? {}) };
      if (val === "" || val == null) {
        delete nextStock[s];
      } else {
        const n = Number(val);
        if (!Number.isNaN(n) && n >= 0) nextStock[s] = n;
      }
      return { ...f, size_stock: nextStock };
    });
  };

  const handleColorImageUpload = async (color: string, file: File, index: number) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file);
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data } = supabase.storage.from("product-images").getPublicUrl(path);
    const current = formRef.current;
    const nextColorImages = { ...(current.color_images ?? {}) };
    const arr = [...(nextColorImages[color] ?? [])];
    arr[index] = data.publicUrl;
    nextColorImages[color] = arr;
    setForm((f: any) => ({ ...f, color_images: nextColorImages }));
    if (current?.id) {
      const colors = current.colors?.length ? current.colors : parseColorsInput(colorsInput);
      const { error: updateError } = await supabase
        .from("products")
        .update({ color_images: cleanColorImages(nextColorImages, colors) })
        .eq("id", current.id);
      if (updateError) toast.error(updateError.message);
    }
    setUploading(false);
  };

  const handleColorImageUploadMany = async (color: string, files: FileList) => {
    setUploading(true);
    const uploaded: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop();
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("product-images").upload(path, file);
      if (error) { toast.error(error.message); continue; }
      const { data } = supabase.storage.from("product-images").getPublicUrl(path);
      uploaded.push(data.publicUrl);
    }
    const current = formRef.current;
    const nextColorImages = { ...(current.color_images ?? {}) };
    nextColorImages[color] = [...(nextColorImages[color] ?? []), ...uploaded];
    setForm((f: any) => ({ ...f, color_images: nextColorImages }));
    if (current?.id) {
      const colors = current.colors?.length ? current.colors : parseColorsInput(colorsInput);
      const { error: updateError } = await supabase
        .from("products")
        .update({ color_images: cleanColorImages(nextColorImages, colors) })
        .eq("id", current.id);
      if (updateError) toast.error(updateError.message);
    }
    setUploading(false);
  };

  const removeColorImage = (color: string, index: number) => {
    const current = formRef.current;
    const map = { ...(current.color_images ?? {}) };
    map[color] = (map[color] ?? []).filter((_: any, i: number) => i !== index);
    setForm((f: any) => ({ ...f, color_images: map }));
    if (current?.id) {
      const colors = current.colors?.length ? current.colors : parseColorsInput(colorsInput);
      supabase
        .from("products")
        .update({ color_images: cleanColorImages(map, colors) })
        .eq("id", current.id)
        .then(({ error }) => error && toast.error(error.message));
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file);
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data } = supabase.storage.from("product-images").getPublicUrl(path);
    setForm((f: any) => ({ ...f, image_url: data.publicUrl }));
    setUploading(false);
  };

  const handleGalleryUpload = async (file: File, index: number) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file);
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data } = supabase.storage.from("product-images").getPublicUrl(path);
    setForm((f: any) => {
      const imgs = [...(f.images ?? [])];
      imgs[index] = data.publicUrl;
      return { ...f, images: imgs };
    });
    setUploading(false);
  };

  const handleGalleryUploadMany = async (files: FileList) => {
    setUploading(true);
    const uploaded: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop();
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("product-images").upload(path, file);
      if (error) { toast.error(error.message); continue; }
      const { data } = supabase.storage.from("product-images").getPublicUrl(path);
      uploaded.push(data.publicUrl);
    }
    setForm((f: any) => ({ ...f, images: [...(f.images ?? []), ...uploaded] }));
    setUploading(false);
  };

  const removeGalleryImage = (index: number) => {
    setForm((f: any) => ({ ...f, images: (f.images ?? []).filter((_: any, i: number) => i !== index) }));
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price are required"); return; }
    setSaving(true);
    const selectedCat = categories.find((c) => c.id === form.category_id);
    const parsedColors = parseColorsInput(colorsInput);
    const parsedSizes = parseSizes(sizesInput || (form.sizes ?? []).join(", "));
    const cleanedSizeStock: Record<string, number> = {};
    for (const s of parsedSizes) {
      const v = (form.size_stock ?? {})[s];
      if (v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v))) {
        cleanedSizeStock[s] = Number(v);
      }
    }
    const payload = {
      name: form.name,
      description: form.description,
      price: Number(form.price),
      stock: Number(form.stock),
      category_id: form.category_id || null,
      category: selectedCat?.name ?? null, // keep legacy text in sync
      image_url: form.image_url || null,
      color_images: cleanColorImages(form.color_images, parsedColors),
      images: (form.images ?? []).filter((u: string) => !!u),
      colors: parsedColors,
      sizes: parsedSizes,
      size_stock: cleanedSizeStock,
      featured: form.featured,
      on_sale: form.on_sale,
      sale_price: form.on_sale ? Number(form.sale_price) : null,
      size_chart_enabled: !!form.size_chart_enabled,
      size_chart_image_url: form.size_chart_image_url || null,
      size_chart: {
        headers: (form.size_chart?.headers ?? []).map((h: string) => String(h ?? "").trim()),
        rows: (form.size_chart?.rows ?? []).map((row: string[]) => row.map((c) => String(c ?? "").trim())),
      },
    };
    const { error } = form.id
      ? await supabase.from("products").update(payload).eq("id", form.id)
      : await supabase.from("products").insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(form.id ? "Product updated" : "Product created");
    setOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("products").delete().eq("id", deleteId);
    if (error) { toast.error(error.message); return; }
    toast.success("Product deleted");
    setDeleteId(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">Manage your catalog</p>
        </div>
        <Button variant="hero" onClick={openCreate}><Plus className="h-4 w-4" /> Add product</Button>
      </div>

      <Card className="border-0 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Product</th>
                <th className="text-left p-4">Category</th>
                <th className="text-right p-4">Price</th>
                <th className="text-right p-4">Stock</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}><td colSpan={5} className="p-4"><Skeleton className="h-12" /></td></tr>
                ))
              ) : products.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No products yet. Add your first one.</td></tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id} className="hover:bg-muted/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-lg bg-secondary overflow-hidden shrink-0">
                          {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center"><ImageIcon className="h-4 w-4 text-muted-foreground" /></div>}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate max-w-xs">{p.name}</p>
                          <div className="flex gap-1 mt-1">
                            {p.featured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">Featured</span>}
                            {p.on_sale && <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/15 text-warning-foreground">Sale</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{categoryName(p.category_id) === "—" ? (p.category || "—") : categoryName(p.category_id)}</td>
                    <td className="p-4 text-right font-medium">{formatCurrency(Number(p.price), symbol)}</td>
                    <td className="p-4 text-right">{p.stock}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Main image</Label>
              <div className="flex items-start gap-4 mt-2">
                <div className="h-24 w-24 rounded-lg bg-secondary overflow-hidden shrink-0">
                  {form.image_url ? <img src={form.image_url} alt="" className="h-full w-full object-contain" /> : <div className="grid h-full w-full place-items-center"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>}
                </div>
                <label className="flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
                  <div className="rounded-lg border-2 border-dashed border-border hover:border-primary transition-smooth cursor-pointer p-6 text-center">
                    {uploading ? "Uploading..." : <><Upload className="h-5 w-5 mx-auto mb-1" /> <p className="text-xs text-muted-foreground">Click to upload</p></>}
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label>Gallery images</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">Upload multiple images. They will appear as thumbnails on the product page.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {(form.images ?? []).map((url: string, i: number) => (
                  <div key={`${url}-${i}`} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border">
                      <img src={url} alt="" className="h-full w-full object-contain" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(i)}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground grid place-items-center shadow"
                      aria-label="Remove image"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary transition-smooth cursor-pointer overflow-hidden bg-secondary grid place-items-center text-muted-foreground">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files?.length && handleGalleryUploadMany(e.target.files)}
                  />
                  <div className="text-center">
                    <Upload className="h-5 w-5 mx-auto" />
                    <p className="text-[10px] mt-1">{uploading ? "Uploading..." : "Add image(s)"}</p>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label>Available colors</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Type colors and separate with commas (e.g. <code>Red, Blue, #00FF00</code>). Names and hex codes both work.
              </p>
              <Input
                value={colorsInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setColorsInput(val);
                  // live-update the chip list as the user types
                  setForm((f: any) => ({ ...f, colors: parseColorsInput(val) }));
                }}
                onBlur={commitColorsInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitColorsInput();
                  } else if (
                    e.key === "Backspace" &&
                    colorsInput === "" &&
                    (form.colors ?? []).length > 0
                  ) {
                    e.preventDefault();
                    const next = form.colors.slice(0, -1);
                    setForm({ ...form, colors: next });
                    setColorsInput(next.join(", "));
                  }
                }}
                placeholder="Red, Blue, #00FF00"
              />
              {(form.colors ?? []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.colors.map((c: string) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-2 rounded-full border bg-secondary/40 pl-2 pr-1 py-1 text-xs"
                    >
                      <span className="h-3 w-3 rounded-full border" style={{ background: c }} />
                      <span>{c}</span>
                      <button
                        type="button"
                        onClick={() => removeColor(c)}
                        className="grid h-5 w-5 place-items-center rounded-full hover:bg-destructive/10 hover:text-destructive transition-smooth"
                        aria-label={`Remove ${c}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Images-per-color removed: colors are now selectable variants only and do not switch product images. */}
            {/* Sizes */}
            <div>
              <Label>Available sizes</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Pick from presets or type your own (comma-separated). Optionally set stock per size.
              </p>
              <div className="space-y-3">
                {PRESET_SIZES.map((group) => (
                  <div key={group.label}>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{group.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {group.values.map((s) => {
                        const active = (form.sizes ?? []).some((x: string) => x.toLowerCase() === s.toLowerCase());
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => toggleSize(s)}
                            className={`rounded-full border-2 px-3 py-1 text-xs font-medium transition-smooth ${
                              active
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-foreground/10 hover:border-primary"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <Input
                className="mt-3"
                value={sizesInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSizesInput(val);
                  setForm((f: any) => ({ ...f, sizes: parseSizes(val) }));
                }}
                onBlur={() => {
                  const parsed = parseSizes(sizesInput);
                  setForm((f: any) => ({ ...f, sizes: parsed }));
                  setSizesInput(parsed.join(", "));
                }}
                placeholder="Custom sizes e.g. 30, 32, Free Size"
              />
              {(form.sizes ?? []).length > 0 && (
                <div className="mt-3 space-y-2 rounded-lg border p-3">
                  <p className="text-xs font-medium text-muted-foreground">Selected sizes (stock optional)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {form.sizes.map((s: string) => (
                      <div key={s} className="flex items-center gap-2 rounded-lg border bg-secondary/30 px-2 py-1.5">
                        <span className="text-xs font-semibold min-w-[2.5rem]">{s}</span>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Qty"
                          className="h-8 text-xs"
                          value={(form.size_stock ?? {})[s] ?? ""}
                          onChange={(e) => setSizeStock(s, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => removeSize(s)}
                          className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-destructive/10 hover:text-destructive transition-smooth"
                          aria-label={`Remove ${s}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Leave Qty empty to use the product's overall stock for that size.</p>
                </div>
              )}
            </div>

            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
                Add product details, features, fabric, and care instructions. HTML tags (h2, h3, p, ul, li, strong) are supported. Use new lines or bullets like <code>•</code> / <code>-</code> for lists.
              </p>
              <Textarea rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={"Premium soft cotton blend.\n\n• Breathable & lightweight\n• Machine washable\n• Imported fabric\n\nCare: wash cold, do not bleach."} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Price</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Category</Label>
                {categories.length === 0 ? (
                  <div className="mt-1 text-xs text-muted-foreground rounded-md border border-dashed p-2">
                    No categories yet.{" "}
                    <Link to="/admin/categories" className="text-primary underline">
                      Create one
                    </Link>
                    .
                  </div>
                ) : (
                  <Select
                    value={form.category_id || undefined}
                    onValueChange={(v) => setForm({ ...form, category_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
              <Label>Featured on homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
              <Label>On sale</Label>
              <Switch checked={form.on_sale} onCheckedChange={(v) => setForm({ ...form, on_sale: v })} />
            </div>
            {form.on_sale && (
              <div><Label>Sale price</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></div>
            )}

            {/* Size chart */}
            <div className="rounded-lg border bg-secondary/30 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Size chart</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Enable and customize a size chart for this product.</p>
                </div>
                <Switch
                  checked={!!form.size_chart_enabled}
                  onCheckedChange={(v) => setForm({ ...form, size_chart_enabled: v })}
                />
              </div>

              {form.size_chart_enabled && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Size chart image (optional)</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5">Upload a measurement chart photo. Shown above the table on the product page.</p>
                    <div className="flex items-start gap-3">
                      <div className="h-24 w-24 rounded-lg bg-secondary overflow-hidden shrink-0 border">
                        {form.size_chart_image_url ? (
                          <img src={form.size_chart_image_url} alt="" className="h-full w-full object-contain" />
                        ) : (
                          <div className="grid h-full w-full place-items-center"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>
                        )}
                      </div>
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploading(true);
                            const ext = file.name.split(".").pop();
                            const path = `size-charts/${crypto.randomUUID()}.${ext}`;
                            const { error } = await supabase.storage.from("product-images").upload(path, file);
                            if (error) { toast.error(error.message); setUploading(false); return; }
                            const { data } = supabase.storage.from("product-images").getPublicUrl(path);
                            setForm((f: any) => ({ ...f, size_chart_image_url: data.publicUrl }));
                            setUploading(false);
                          }}
                        />
                        <div className="rounded-lg border-2 border-dashed border-border hover:border-primary transition-smooth cursor-pointer p-4 text-center">
                          {uploading ? "Uploading..." : <><Upload className="h-5 w-5 mx-auto mb-1" /><p className="text-xs text-muted-foreground">Click to upload chart image</p></>}
                        </div>
                      </label>
                      {form.size_chart_image_url && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => setForm({ ...form, size_chart_image_url: "" })} aria-label="Remove">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Column headers (comma-separated)</Label>
                    <Input
                      placeholder="Size, Chest, Length"
                      value={(form.size_chart?.headers ?? []).join(", ")}
                      onChange={(e) => {
                        const headers = e.target.value.split(",").map((s) => s.trim());
                        setForm((f: any) => ({
                          ...f,
                          size_chart: {
                            headers,
                            rows: (f.size_chart?.rows ?? []).map((r: string[]) => {
                              const next = [...r];
                              while (next.length < headers.length) next.push("");
                              return next.slice(0, headers.length);
                            }),
                          },
                        }));
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Rows</Label>
                    {(form.size_chart?.rows ?? []).map((row: string[], ri: number) => (
                      <div key={ri} className="flex items-center gap-2">
                        <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: `repeat(${Math.max(1, (form.size_chart?.headers ?? []).length)}, minmax(0, 1fr))` }}>
                          {(form.size_chart?.headers ?? []).map((_: string, ci: number) => (
                            <Input
                              key={ci}
                              className="h-9"
                              value={row[ci] ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setForm((f: any) => {
                                  const rows = [...(f.size_chart?.rows ?? [])];
                                  const newRow = [...(rows[ri] ?? [])];
                                  newRow[ci] = val;
                                  rows[ri] = newRow;
                                  return { ...f, size_chart: { ...f.size_chart, rows } };
                                });
                              }}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f: any) => ({
                              ...f,
                              size_chart: {
                                ...f.size_chart,
                                rows: (f.size_chart?.rows ?? []).filter((_: any, i: number) => i !== ri),
                              },
                            }))
                          }
                          className="grid h-8 w-8 place-items-center rounded-full hover:bg-destructive/10 hover:text-destructive transition-smooth"
                          aria-label="Remove row"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setForm((f: any) => {
                          const headers = f.size_chart?.headers ?? [];
                          return {
                            ...f,
                            size_chart: {
                              headers,
                              rows: [...(f.size_chart?.rows ?? []), Array(Math.max(1, headers.length)).fill("")],
                            },
                          };
                        })
                      }
                    >
                      <Plus className="h-4 w-4" /> Add row
                    </Button>
                  </div>
                </div>
              )}
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="hero" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete product?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProducts;
